// eslint-disable-next-line no-unused-vars
const types = require('../../../../../../jsdoc-types');
const { createEcommerceEventItemsGA4 } = require('./ecommerceEventItemsGA4');
const { createEcommerceEventGA4 } = require('./ecommerceEventGA4');
const { parseGa4PageType } = require('../constants');
const analyticUtils = require('../../analyticUtils');
const { getCartFromLocalStorage } = require('../../../cart/localCart');
const { GA4 } = require('../../../../../../config/analytics');
const { searchDataOfCartItemsStorage } = require('../../stores/searchData');
const { getPaymentTypeForAnalytics } = require('../../helpers/paymentType');
const { cartItemListStorage } = require('../../stores/itemList');

/**
 * @typedef {object} AbstractEcommerceCheckoutEventGA4
 * @property {object} dl_ga4_ecommerce Ecommerce trigger variable
 * @property {string} currency Currency of the site
 * @property {string} value Value of the product
 * @property {Array} items Array of objects that contains all extra info
 */

/**
 * Ecommerce Event for Google Analytics 4.
 * It contains general variables and dl_ga4_ecommerce.
 * @typedef {import('./eventGA4').EventGA4 & AbstractEcommerceCheckoutEventGA4} EcommerceCheckoutEventGA4
 */

/**
 * @param {string} name The name of the event
 * @param {types.Order} order The Order for the event building
 * @returns {EcommerceCheckoutEventGA4|null} object with all the params that are needed
 */
function createEcommerceCheckoutEventGA4(name, order) {
  let productLineItems = order
    ? order.items.items
    : getCartFromLocalStorage()?.items;

  if ((productLineItems?.length ?? 0) === 0) return null;

  const dimensions = analyticUtils.getDimensionsFromLocalStorage();

  // Add itemProps
  productLineItems = productLineItems.map((item) => {
    const currentDimension = dimensions.find((hit) => hit.pid === item.id);

    const { size } = analyticUtils.getDisplayValuesOfVariationAttributes(
      item.variationAttributes
    );

    return {
      ...item,
      itemProps: {
        add_to_cart_place:
          parseGa4PageType(currentDimension?.d38) || GA4.PAGE_TYPES.product,
        product_size: size,
      },
    };
  });

  const items = createEcommerceEventItemsGA4(productLineItems, {
    itemListStorage: cartItemListStorage,
    getItemListStorageKey: (product) => product.id,
    targetSearchDataStorage: searchDataOfCartItemsStorage,
  });

  // Check if payment method already exists
  const paymentInstrument =
    order?.billing?.payment?.selectedPaymentInstruments[0];

  const isExpress = order?.isExpressCheckoutOrder;

  const ecommerceProps = {
    currency: gaInfo.currencyCode,
    value: order
      ? parseFloat(order?.totals?.floatTotal).toFixed(2)
      : gaInfo.cart_total_value,
    payment_type: paymentInstrument
      ? getPaymentTypeForAnalytics(paymentInstrument)
      : undefined,
    payment_type_express: isExpress ? 'cart' : undefined,
    shipping_tier:
      order?.shipping[0]?.selectedShippingMethod?.ID || undefined,
    shipping:
      order?.totals?.totalShippingCostValue?.toFixed(2) || undefined,
    coupon: order?.totals?.discounts?.couponCode || undefined,
  };

  if (order?.orderNumber) {
    ecommerceProps.transaction_id = order.orderNumber;
  }

  const additionalProps = {};
  if (name === 'purchase') {
    // Facebook event
    additionalProps.fb_am = gaInfo.userInfo || {
      country:
        order?.billing?.billingAddress?.address.countryCode.value.toLowerCase(),
      db: undefined,
      em: order?.orderEmail,
      fn: order?.billing?.billingAddress?.address.firstName.toLowerCase(),
      ge: undefined,
      ln: order?.billing?.billingAddress?.address.lastName.toLowerCase(),
      ph: order?.billing?.billingAddress?.address.phone.replace(/[^\d,]/g, ''),
    };
  }

  return createEcommerceEventGA4(name, items, ecommerceProps, additionalProps);
}

module.exports = {
  createEcommerceCheckoutEventGA4,
};
